import { useMemo, useState } from "react";
import {
  useClerk,
  useOrganization as useClerkOrganization,
} from "@clerk/clerk-react";
import { Select, Button, Avatar, Divider, Badge, message } from "antd";
import {
  TeamOutlined,
  PlusOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useModalStore } from "../../hooks/useModalStore";
import { useAllOrganizations } from "../../hooks/useAllOrganizations";

function CustomOrganizationSwitcher() {
  const { setShowCreateOrg, setShowManageOrg } = useModalStore();
  const { setActive } = useClerk();
  const { organization, membership } = useClerkOrganization();
  
  // 使用封装的 useAllOrganizations hook 获取所有组织列表
  const {
    orgListOptions,
    userInvitations,
    refresh,
    invitationCount,
  } = useAllOrganizations();

  // 获取当前选中的组织选项
  const currentOrg = useMemo(() => {
    return orgListOptions.find((opt) => opt.value === organization?.id) || null;
  }, [orgListOptions, organization?.id]);

  const handleSwitch = async (organizationId: string | null) => {
    await setActive({ organization: organizationId });
    window.location.replace("/");
  };

  // 展示条件和下拉选项使用同一份 membership 数据。
  if (orgListOptions.length <= 1) {
    return null;
  }

  return (
    <Badge count={invitationCount} color="#552eef">
      <Select
        defaultActiveFirstOption={false}
        className="w-[200px]"
        value={organization?.id || null}
        onChange={handleSwitch}
        options={orgListOptions}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
        }
        dropdownRender={(menu) => (
          <div className="relative">
            {/* 当前组织 */}
            <OrganizationComponent
              currentOrg={currentOrg}
              membership={membership}
              setShowManageOrg={setShowManageOrg}
            />
            {/*  */}
            {/* 邀请列表 */}
            {userInvitations?.data?.map((invitation) => (
              <InviteItemComponent
                key={invitation.id}
                invitation={invitation}
                refresh={refresh}
              />
            ))}
            <div className="bg-white rounded-md shadow-md mt-1">
              {/* 组织列表选择器 */}
              {menu}
              {/* 功能区域 */}
              <Divider className="mt-2 mb-0" />
              <div className="">
                <div
                  className="flex h-[60px] items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-gray-50"
                  onClick={() => setShowCreateOrg(true)}
                >
                  <PlusOutlined className="text-gray-500" />
                  <span className="text-gray-700">Create organization</span>
                </div>
              </div>
            </div>
          </div>
        )}
        //
        optionRender={(option) => (
          <div className="flex items-center gap-2 min-w-0 py-3">
            <span className="text-gray-700 text-xl flex-shrink-0">
              {option.data.icon}
            </span>
            <span className="text-gray-700 truncate">{option.data.label}</span>
          </div>
        )}
        labelRender={(option) => {
          const currentOrg: any =
            orgListOptions.find((opt) => opt.value === option.value) || {};
          // console.log("currentOrg", currentOrg);
          const currentOrgIconUrl = currentOrg?.icon?.props?.src;
          return (
            <div className="flex items-center gap-2">
              <span className="text-gray-700 text-sm flex-shrink-0 ml-1">
                {currentOrgIconUrl ? (
                  <Avatar
                    size={24}
                    shape="square"
                    src={currentOrgIconUrl}
                    className="rounded"
                  />
                ) : (
                  <TeamOutlined className="text-gray-500 text-xl" />
                )}
              </span>
              <span className="text-gray-700 truncate">
                {currentOrg?.label}
              </span>
              {/* <span className="text-gray-700 truncate">{option.value}</span> */}
            </div>
          );
        }}
      />
    </Badge>
  );
}

export default CustomOrganizationSwitcher;

const InviteItemComponent = ({
  invitation,
  refresh,
}: {
  invitation: any;
  refresh: () => void;
}) => {
  const [buttonLoading, setButtonLoading] = useState(false);
  return (
    <div
      className="flex items-center gap-2 p-2 rounded-md cursor"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="text-gray-700 text-xl flex-shrink-0">
          <Avatar
            size={32}
            shape="square"
            src={invitation?.publicOrganizationData?.imageUrl}
            className="rounded"
          />
        </span>
        <div className="flex flex-col w-[160px]">
          <span className="text-gray-500 text-sm truncate">
            {invitation?.publicOrganizationData?.name || ""}
          </span>
          <span className="text-gray-700 truncate">
            {invitation?.publicOrganizationData?.role
              ? invitation?.publicOrganizationData.role
                  .replace("org:", "")
                  .charAt(0)
                  .toUpperCase() +
                invitation?.publicOrganizationData.role
                  .replace("org:", "")
                  .slice(1)
              : ""}{" "}
            <span className="text-gray-500 text-sm">inviting</span>
          </span>
        </div>
      </div>
      {invitation?.accept && (
        <Button
          loading={buttonLoading}
          type="text"
          size="small"
          className="border border-gray-300 rounded-md"
          onClick={async (e) => {
            e.stopPropagation();
            // 列表里面带着 accept 方法
            try {
              setButtonLoading(true);
              const res = await invitation.accept();
              // console.log("res", res);
              if (res?.status === "accepted") {
                message.success("Successfully joined the organization");
              }
            } catch (error) {
              message.error("Failed to join the organization");
            } finally {
              setButtonLoading(false);
            }
            // 刷新组织列表
            refresh();
          }}
        >
          Join
        </Button>
      )}
    </div>
  );
};

const OrganizationComponent = ({
  currentOrg,
  membership,
  setShowManageOrg,
}: {
  currentOrg: any;
  membership: any;
  setShowManageOrg: (show: boolean) => void;
}) => {
  return (
    <>
      <div
        className="flex items-center gap-2 p-2 rounded-md cursor"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-gray-700 text-xl flex-shrink-0">
            {currentOrg?.icon}
          </span>
          <div className="flex flex-col w-[160px]">
            <span className="text-gray-500 text-sm truncate">
              {currentOrg?.label || ""}
            </span>
            <span className="flex items-center gap-2 text-gray-700 truncate">
              {membership?.role
                ? membership.role.replace("org:", "").charAt(0).toUpperCase() +
                  membership.role.replace("org:", "").slice(1)
                : ""}
              {
                <Button
                  type="text"
                  size="small"
                  className="border border-gray-300 rounded-md"
                  icon={<SettingOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowManageOrg(true);
                  }}
                >
                  Manage
                </Button>
              }
            </span>
          </div>
        </div>
      </div>
    </>
  );
};
